import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getAllPosts } from "@/lib/posts.functions";
import { getTag, tagLabel, TAGS, type TagDef } from "@/lib/tags";
import { pageTitle } from "@/lib/brand";
import { absUrl, hreflangLinks } from "@/lib/seo";
import { listPublishedReports } from "@/lib/reports.functions";
import { buildEnTopics, enTopicSlug } from "@/lib/en-topics";
import { PageIntro, PageSection } from "@/components/site/page";
import { ArchiveRows, postsToArchiveItems } from "@/components/site/ArchiveRows";
import { listApprovedThemes } from "@/lib/themes-public.functions";

export const Route = createFileRoute("/etiket/$slug")({
  loader: async ({ params }) => {
    const slug = params.slug;
    const [posts, themes] = await Promise.all([getAllPosts({ data: { lang: "tr" } }), listApprovedThemes()]);
    const matching = posts.filter((p) => (p.tags ?? []).includes(slug));
    // Curated tags always resolve; a free tag only gets a page while at least
    // one post carries it, so no empty archive is ever indexable.
    const known = getTag(slug);
    if (!known && matching.length === 0) throw notFound();
    const label = tagLabel(slug, "tr");
    const tag: TagDef =
      known ??
      ({
        slug,
        tr: label,
        en: label,
        descTr: `“${label}” etiketiyle işaretlenmiş yazılar.`,
        descEn: `Essays tagged “${label}”.`,
        match: [],
      } satisfies TagDef);
    // Only emit the English alternate when the English hub really exists.
    let hasEn = false;
    try {
      hasEn = buildEnTopics(await listPublishedReports()).has(slug);
    } catch {
      hasEn = false;
    }
    return { tag, posts: matching, hasEn, themes };
  },

  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: pageTitle("Etiket bulunamadı", "tr") },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { tag, posts } = loaderData;
    const desc = `${tag.descTr} ${posts.length} yazı.`;
    const path = `/etiket/${tag.slug}`;
    return {
      meta: [
        { title: pageTitle(tag.tr, "tr") },
        { name: "description", content: desc },
        { property: "og:title", content: `${tag.tr} — Akıllı Sürdürülebilirlik` },
        { property: "og:description", content: desc },
        { property: "og:url", content: absUrl(path) },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
      ],
      links: [{ rel: "canonical", href: absUrl(path) }, ...hreflangLinks(path, loaderData.hasEn ? `/topics/${enTopicSlug(tag.slug)}` : undefined)],
    };
  },
  component: TagPage,
  notFoundComponent: TagNotFound,
});

function TagNotFound() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-24 text-center">
      <h1 className="font-serif text-4xl">Etiket bulunamadı</h1>
      <p className="mt-4 text-ink/70">
        Bu etiket tanımlı listede yok.{" "}
        <Link to="/etiket" className="text-theme-tech underline">
          Tüm etiketler
        </Link>
      </p>
    </div>
  );
}

function TagPage() {
  const { tag, posts, themes } = Route.useLoaderData();
  const others = TAGS.filter((t) => t.slug !== tag.slug);
  const items = postsToArchiveItems(posts, themes);

  return (
    <>
      <PageIntro
        crumbs={[{ label: "Etiketler", to: "/etiket" }, { label: tag.tr }]}
        kicker={`${posts.length} yazı`}
        title={tag.tr}
        lede={tag.descTr}
      />
      <PageSection>
        {posts.length === 0 ? (
          <p className="border-y border-ink/10 py-10 text-ink/70">Bu etikette henüz yazı yok.</p>
        ) : (
          <div className="border-t border-ink/10"><ArchiveRows items={items} /></div>
        )}
      </PageSection>
      <PageSection title="Diğer etiketler">
        <div className="flex flex-wrap gap-2">
          {others.map((t) => (
            <Link
              key={t.slug}
              to="/etiket/$slug"
              params={{ slug: t.slug }}
              className="inline-flex min-h-10 items-center rounded-full border border-ink/20 px-4 text-[13px] text-ink/80 transition-colors hover:border-ink hover:text-ink"
            >
              {t.tr}
            </Link>
          ))}
        </div>
      </PageSection>
    </>
  );
}
